import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Wrapper } from '@googlemaps/react-wrapper';
import { createCustomEqual, CustomEqualCreatorOptions } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import { useTranslation } from 'react-i18next';

// Types
import { MapProps, MapComponentProps, MarkerProps } from './types';

// El marcador rico de `_internal` (dot de 20 px con área táctil de 44 px) —
// el mismo que usan MultiPathMap y el mapa del RV. Se reutiliza aquí para el
// punto de una ubicación puntual en vez de escribir un tercer marcador.
import { Marker as DotMarker } from './_internal';

// Styled Components
import {
  ContainerMap,
  FullscreenLayer,
  MapControlButton,
  MapControls,
  styleMap,
} from './styles';

// Iconos de los mapas de ROIS (Lucide, ISC): los mismos que el mapa del RV.
import { LocateIcon } from '../../../assets/icons/LocateIcon';
import { MaximizeIcon } from '../../../assets/icons/MaximizeIcon';
import { MinimizeIcon } from '../../../assets/icons/MinimizeIcon';

import { useTheme } from 'styled-components';
import { useLanguage } from '../../../hooks/useLanguage';
import { useFullscreenToggle } from '../../../hooks/useFullscreenToggle';
import {
  MAP_LIBRARIES,
  MAP_REGION,
  mapsLanguage,
} from './mapsLoaderOptions';

// AdvancedMarkerElement requiere cargar la librería 'marker' y un mapId en el mapa
const MAP_ID = import.meta.env.VITE_MAP_ID || 'DEMO_MAP_ID';

// `path` del Symbol: solo actúa de type-guard `'path' in icon`; el dibujo
// real del dot lo decide `createMarkerContentFromSymbol` a partir de los
// colores. Mismo valor que el `DOT_SYMBOL_PATH` de MapWithSegments.
const DOT_SYMBOL_PATH = 'M-10,0 A10,10 0 1 1 10,0 A10,10 0 0 1 -10,0';

/**
 * A pantalla completa sí hay controles: + y - (el pellizco no siempre es
 * cómodo con guantes) y Mapa / Satélite (el satélite enseña el sitio real:
 * arcén, cuneta, talud). Un dedo mueve el mapa, porque ya no hay página que
 * desplazar. En pequeño sigue sin controles de Google, como siempre.
 */
const FULLSCREEN_MAP_OPTIONS: google.maps.MapOptions = {
  gestureHandling: 'greedy',
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: { mapTypeIds: ['roadmap', 'hybrid'] },
  streetViewControl: false,
  fullscreenControl: false,
};

/** Sin esquinas redondeadas a pantalla completa. */
const FULLSCREEN_STYLE = { ...styleMap, borderRadius: '0' };

type MapCanvasProps = Omit<MapComponentProps, 'allowFullscreen'> & {
  fullscreen?: boolean;
  onMapReady?: (map: google.maps.Map) => void;
};

/** Un mapa de Google con el punto (o el trazado) y la posición del usuario. */
const MapCanvas = ({
  zoom,
  center,
  path,
  point,
  geoloc,
  apiKey,
  fullscreen = false,
  onMapReady,
}: MapCanvasProps) => {
  const theme = useTheme();
  const { currentLanguage } = useLanguage();
  const extraOptions = fullscreen ? FULLSCREEN_MAP_OPTIONS : {};

  return (
    <Wrapper
      apiKey={apiKey}
      language={mapsLanguage(currentLanguage)}
      region={MAP_REGION}
      libraries={MAP_LIBRARIES}
    >
      <Map
        center={center}
        zoom={zoom}
        style={fullscreen ? FULLSCREEN_STYLE : styleMap}
        clickableIcons={false}
        disableDefaultUI={true}
        onReady={onMapReady}
        {...extraOptions}
      >
        {point ? (
          <DotMarker
            position={point}
            symbolIcon={{
              path: DOT_SYMBOL_PATH,
              fillColor: theme.colors.garnet as string,
              strokeColor: theme.colors.white as string,
              strokeWeight: 2,
            }}
          />
        ) : (
          <Path
            path={path}
            geodesic={true}
            strokeColor={theme.colors.garnet as string}
            strokeOpacity={1.0}
            strokeWeight={8}
          />
        )}
        {geoloc && <Marker position={geoloc} />}
      </Map>
    </Wrapper>
  );
};

/**
 * Mapa compartido de ROIS: ubicación de una actuación, de una foto, vista
 * previa del panel… Con `allowFullscreen` lleva dos botones, como el mapa del
 * recorrido: **recentrar** (vuelve al punto o al trazado tras moverse) y
 * **pantalla completa** (y de vuelta a su sitio con el mismo botón, Escape o
 * «atrás» de Android).
 *
 * Se enciende solo donde se lee (visor de fotos, resumen de la actuación). En
 * los formularios no: tapar la pantalla a quien rellena estorbaría — el mismo
 * criterio que el mapa del recorrido en su asistente.
 *
 * Los botones están FUERA del mapa de Google: existen aunque el mapa no
 * cargue (la E2E corre sin la API de Google), y recentrar sin mapa no hace
 * nada.
 */
export const MapComponent = ({
  allowFullscreen = false,
  ...canvasProps
}: MapComponentProps) => {
  const { t } = useTranslation(['pages']);
  const { fullscreen, open, close } = useFullscreenToggle(allowFullscreen);
  const inlineMap = useRef<google.maps.Map>();
  const fullscreenMap = useRef<google.maps.Map>();
  const layerRef = useRef<HTMLDivElement>(null);
  const { center, zoom } = canvasProps;

  const recenter = useCallback(
    (map: google.maps.Map | undefined) => {
      if (!map) return;
      map.setCenter(center);
      if (zoom !== undefined) map.setZoom(zoom);
    },
    [center, zoom],
  );

  // 🔴 El mapa grande vive en el body, fuera del modal que lo contiene. Un
  // modal de ROIS se cierra al pulsar FUERA de él (`useClickAway` de
  // BaseModal, que escucha mousedown y touchstart en el documento): sin esto,
  // el primer toque en el mapa grande cerraba el visor de fotos. Se cortan
  // aquí, después de que el mapa los haya recibido.
  useEffect(() => {
    const layer = layerRef.current;
    if (!fullscreen || !layer) return undefined;
    const stop = (event: Event): void => event.stopPropagation();
    layer.addEventListener('mousedown', stop);
    layer.addEventListener('touchstart', stop);
    return () => {
      layer.removeEventListener('mousedown', stop);
      layer.removeEventListener('touchstart', stop);
    };
  }, [fullscreen]);

  return (
    <ContainerMap>
      <MapCanvas
        {...canvasProps}
        onMapReady={(map) => {
          inlineMap.current = map;
        }}
      />
      {allowFullscreen && (
        <MapControls>
          <MapControlButton
            type="button"
            onClick={() => recenter(inlineMap.current)}
            aria-label={t('pages:surveillance.mapCenter')}
            data-testid="map-recenter"
          >
            <LocateIcon />
          </MapControlButton>
          <MapControlButton
            type="button"
            onClick={open}
            aria-label={t('pages:surveillance.mapFullscreen')}
            data-testid="map-expand"
          >
            <MaximizeIcon />
          </MapControlButton>
        </MapControls>
      )}
      {fullscreen &&
        createPortal(
          <FullscreenLayer
            ref={layerRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('pages:surveillance.mapFullscreen')}
            data-map-fullscreen="true"
          >
            <MapCanvas
              {...canvasProps}
              fullscreen
              onMapReady={(map) => {
                fullscreenMap.current = map;
              }}
            />
            <MapControls>
              <MapControlButton
                type="button"
                onClick={() => recenter(fullscreenMap.current)}
                aria-label={t('pages:surveillance.mapCenter')}
                data-testid="map-fullscreen-recenter"
              >
                <LocateIcon />
              </MapControlButton>
              <MapControlButton
                type="button"
                onClick={close}
                aria-label={t('pages:surveillance.mapExitFullscreen')}
                data-testid="map-collapse"
              >
                <MinimizeIcon />
              </MapControlButton>
            </MapControls>
          </FullscreenLayer>,
          document.body,
        )}
    </ContainerMap>
  );
};

const Map: React.FC<MapProps> = ({
  onClick,
  onIdle,
  onReady,
  children,
  style,
  ...options
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  // Ref: quien lo pasa suele dar una función nueva en cada render.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (ref.current && !map) {
      // mapId solo puede fijarse en la construcción, no via setOptions
      setMap(new window.google.maps.Map(ref.current, { mapId: MAP_ID }));
    }
  }, [ref, map]);

  useEffect(() => {
    if (map) onReadyRef.current?.(map);
  }, [map]);

  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component

          // @ts-ignore
          return React.cloneElement(child, { map });
        }
      })}
    </>
  );
};

const Path: React.FC<google.maps.PolylineOptions> = ({ ...options }) => {
  const [path, setPath] = useState<google.maps.Polyline>();

  useEffect(() => {
    if (!path) {
      setPath(new google.maps.Polyline());
    }

    return () => {
      if (path) {
        path.setMap(null);
      }
    };
  }, [path]);

  useEffect(() => {
    if (path) {
      path.setOptions(options);
    }
  }, [path, options]);

  return null;
};

// Réplica del antiguo icon Symbol (círculo blanco de 20px centrado en la posición);
// translateY(50%) compensa el anclaje bottom-center de AdvancedMarkerElement
const createMarkerContent = () => {
  const content = document.createElement('div');
  content.style.width = '20px';
  content.style.height = '20px';
  content.style.borderRadius = '50%';
  content.style.backgroundColor = 'white';
  content.style.border = '1px solid black';
  content.style.transform = 'translateY(50%)';
  return content;
};

const Marker: React.FC<MarkerProps> = ({ position, map }) => {
  const [marker, setMarker] =
    useState<google.maps.marker.AdvancedMarkerElement>();

  useEffect(() => {
    if (!marker) {
      setMarker(
        new google.maps.marker.AdvancedMarkerElement({
          content: createMarkerContent(),
        })
      );
    }

    return () => {
      if (marker) {
        marker.map = null;
      }
    };
  }, [marker]);

  useEffect(() => {
    if (marker) {
      marker.position = position;
      marker.map = map ?? null;
    }
  }, [marker, position, map]);

  return null;
};

const deepCompareEqualsForMaps = createCustomEqual(((
    deepEqual: <A, B>(a: A, b: B) => boolean
  ) =>
  (a: google.maps.LatLng, b: google.maps.LatLng) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }
    return deepEqual(a, b);
  }) as CustomEqualCreatorOptions<undefined>);

function useDeepCompareMemoize(value: object | undefined) {
  const ref = React.useRef<object>();
  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: [google.maps.Map | undefined, google.maps.MapOptions]
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
